import React, { useState, useEffect, useRef, useCallback } from 'react';

// Mole component
function Mole({ isUp, onWhack, delay, moleId }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isUp) {
      setVisible(true);
      // Set a timer for the mole to go down after a delay
      timerRef.current = setTimeout(() => {
        setVisible(false);
      }, delay);
    } else {
      setVisible(false);
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [isUp, delay]);

  const handleWhack = () => {
    if (visible) {
      onWhack(moleId);
      setVisible(false); // Make it disappear immediately on whack
      clearTimeout(timerRef.current); // Clear its natural disappearance timer
    }
  };

  return (
    <div
      className={`
        relative w-20 h-20 sm:w-24 sm:h-24 md:w-24 md:h-24 lg:w-24 lg:h-24
        bg-amber-700 rounded-full flex items-end justify-center overflow-hidden
        shadow-inner transition-all duration-300 ease-in-out
        border-4 border-amber-800
      `}
      // This div acts as the hole
    >
      <div
        onClick={handleWhack}
        className={`
          absolute w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-30 lg:h-30
          bg-gray-700 rounded-full flex items-center justify-center
          transform transition-transform duration-200 ease-out cursor-pointer
          ${visible ? 'translate-y-0' : 'translate-y-full'}
          ${visible ? 'hover:scale-105 active:scale-95' : 'pointer-events-none'}
          drop-shadow-lg
        `}
        style={{ top: visible ? 'calc(50% - 1.5rem)' : '100%', left: '50%', transform: `translateX(-50%) ${visible ? 'translateY(0)' : 'translateY(50%)'}` }}
      >
        {/* Mole SVG or Emoji */}
        <span className="text-5xl sm:text-6xl md:text-7xl">
          😈 {/* Using a simple emoji for the mole */}
        </span>
      </div>
    </div>
  );
}

// Main App component for the Whac-A-Mole game
function MoleGame() {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30); // Game duration in seconds
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeMoleId, setActiveMoleId] = useState(null); // The ID of the mole currently "up"
  const gameTimerRef = useRef(null);
  const moleTimerRef = useRef(null);

  const numberOfHoles = 9; // 3x3 grid
  const moleUpDelay = 1000; // How long a mole stays up (ms)
  const moleInterval = 800; // Interval between moles popping up (ms)

  // Start the game
  const startGame = useCallback(() => {
    setScore(0);
    setTimeLeft(30);
    setIsPlaying(true);
    setActiveMoleId(null);

    // Main game timer
    gameTimerRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(gameTimerRef.current);
          clearInterval(moleTimerRef.current);
          setIsPlaying(false);
          setActiveMoleId(null);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Mole popping logic
    moleTimerRef.current = setInterval(() => {
      // Clear previous active mole (if any) to prevent multiple moles up at once
      setActiveMoleId(null);
      setTimeout(() => { // Give a small buffer for mole to go down visually
        const randomIndex = Math.floor(Math.random() * numberOfHoles);
        setActiveMoleId(randomIndex);
      }, 200); // Short delay to ensure previous mole is down
    }, moleInterval);
  }, [moleInterval, numberOfHoles]);

  // Stop all timers when component unmounts or game ends
  useEffect(() => {
    return () => {
      clearInterval(gameTimerRef.current);
      clearInterval(moleTimerRef.current);
    };
  }, []);

  // Handle mole whack
  const handleWhack = useCallback((whackedMoleId) => {
    if (isPlaying && whackedMoleId === activeMoleId) {
      setScore(prevScore => prevScore + 1);
      setActiveMoleId(null); // Make the mole disappear immediately
    }
  }, [isPlaying, activeMoleId]);

  return (
    <div className=" bg-gradient-to-br from-yellow-200 via-orange-300 to-red-200 flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-white bg-opacity-90 p-6 sm:p-8 md:p-8 rounded-3xl shadow-2xl transform hover:scale-[1.01] transition-transform duration-300 ease-in-out flex flex-col items-center border border-gray-100 backdrop-blur-sm">
        <h1 className="text-2xl sm:text-4xl md:text-4xl font-extrabold text-gray-800 mb-6 text-center tracking-tight leading-tight drop-shadow-md">
          Whac-A-Mole
        </h1>

        <div className="mb-6 flex space-x-8 text-xl sm:text-2xl font-bold text-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-green-600">Score:</span>
            <span className="text-2xl font-extrabold text-green-700">{score}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-red-600">Time Left:</span>
            <span className="text-2xl font-extrabold text-red-700">{timeLeft}s</span>
          </div>
        </div>

        {!isPlaying && timeLeft === 0 && (
          <div className="text-3xl sm:text-4xl font-bold mb-6 text-gray-700 animate-fade-in-up">
            Game Over! Your Score: {score}
          </div>
        )}
        {!isPlaying && timeLeft > 0 && (
            <div className="text-2xl sm:text-2xl font-bold mb-6 text-gray-700">
                Click Start to Play!
            </div>
        )}
        {isPlaying && (
            <div className="text-2xl sm:text-3xl font-bold mb-6 text-gray-700 animate-pulse">
                Whack the Moles!
            </div>
        )}


        <div className="grid grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-7 p-2 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl shadow-inner">
          {Array.from({ length: numberOfHoles }).map((_, i) => (
            <Mole
              key={i}
              moleId={i}
              isUp={activeMoleId === i}
              onWhack={handleWhack}
              delay={moleUpDelay}
            />
          ))}
        </div>

        <button
          onClick={startGame}
          disabled={isPlaying && timeLeft > 0}
          className={`
            mt-8 px-8 py-4 bg-gradient-to-r from-green-500 to-lime-500 text-white font-bold text-xl rounded-full shadow-lg transform transition-all duration-300
            hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-green-300
            ${isPlaying && timeLeft > 0 ? 'opacity-50 cursor-not-allowed' : 'animate-bounce-once'}
          `}
        >
          {isPlaying ? 'Playing...' : 'Start Game'}
        </button>

        {/* Confetti effect on Game Over if score is high (example threshold) */}
        {!isPlaying && timeLeft === 0 && score >= 5 && (
          <>
            {Array.from({ length: 70 }).map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: `hsl(${Math.random() * 360}, 70%, 60%)`,
                  animationDelay: `${Math.random() * 2}s`,
                  transform: `scale(${0.5 + Math.random()}) rotate(${Math.random() * 360}deg)`,
                  width: `${8 + Math.random() * 12}px`,
                  height: `${8 + Math.random() * 12}px`,
                }}
              ></div>
            ))}
          </>
        )}
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800;900&display=swap');
        body {
          font-family: 'Inter', sans-serif;
        }

        .animate-fade-in-up {
            animation: fadeInScaleUp 0.5s ease-out forwards;
        }

        @keyframes fadeInScaleUp {
            from {
                opacity: 0;
                transform: translateY(20px) scale(0.9);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }

        .animate-bounce-once {
            animation: bounceOnce 1s ease-in-out;
        }

        @keyframes bounceOnce {
            0%, 100% {
                transform: translateY(0);
            }
            20% {
                transform: translateY(-10px);
            }
            40% {
                transform: translateY(0);
            }
            60% {
                transform: translateY(-5px);
            }
            80% {
                transform: translateY(0);
            }
        }

        .confetti-piece {
          position: absolute;
          border-radius: 50%;
          opacity: 0;
          animation: fall 3s ease-out forwards;
          z-index: 10;
        }

        @keyframes fall {
          0% {
            opacity: 0;
            transform: translateY(-100px) rotateZ(0deg) scale(0.5);
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 200px)) rotateZ(720deg) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

// Make sure to load Tailwind CSS in your public/index.html or main HTML file
// <script src="https://cdn.tailwindcss.com"></script>
export default MoleGame;
